import {
  STATUS_COLOR_CODE,
  STATUS_COLOR,
  VIOLATION_STATUS,
  getViolationStatusDisplayValue,
} from '../../configs';
import { dateTimeFormatter, localeFormatter } from '../../utils/formatters';
import { isCoordinatePair } from '../../utils/utils';

export const EVACUATION_APPLICATION_DEFAULT_COLOR = '#343a40';

export const EVACUATION_APPLICATION_STATUS_COLORS = {
  [VIOLATION_STATUS.CREATED]: STATUS_COLOR_CODE[STATUS_COLOR.BLUE],
  [VIOLATION_STATUS.ISSUED]: STATUS_COLOR_CODE[STATUS_COLOR.GREEN],
  [VIOLATION_STATUS.IN_PROCESS]: STATUS_COLOR_CODE[STATUS_COLOR.GREEN],
  [VIOLATION_STATUS.INSPECTED]: STATUS_COLOR_CODE[STATUS_COLOR.GREEN],
  [VIOLATION_STATUS.UPLOADED]: STATUS_COLOR_CODE[STATUS_COLOR.GREEN],
  [VIOLATION_STATUS.APPROVED]: STATUS_COLOR_CODE[STATUS_COLOR.GREEN],
  [VIOLATION_STATUS.CANCELLED]: STATUS_COLOR_CODE[STATUS_COLOR.GREEN],
};

const MOSCOW_CENTER_LON_LAT = [37.6173, 55.7558];

const parseJsonObject = value => {
  if (!value || typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    return value;
  }
};

const toNumberPair = value => {
  let rawValue = value;
  if (typeof rawValue === 'string') {
    rawValue = rawValue.split(/[,\s]+/).map(item => item.trim()).filter(Boolean);
  }
  if (!isCoordinatePair(rawValue)) {
    return null;
  }
  return [Number(rawValue[0]), Number(rawValue[1])];
};

const isLonLat = value => {
  const pair = toNumberPair(value);
  return Boolean(pair && pair[0] >= -180 && pair[0] <= 180 && pair[1] >= -90 && pair[1] <= 90);
};

const isLatLon = value => {
  const pair = toNumberPair(value);
  return Boolean(pair && pair[0] >= -90 && pair[0] <= 90 && pair[1] >= -180 && pair[1] <= 180);
};

const fromLonLat = value => {
  const pair = toNumberPair(value);
  if (!pair || !isLonLat(pair)) {
    return null;
  }
  return {
    lonLat: pair,
    mapCoordinates: [pair[1], pair[0]],
  };
};

const fromLatLon = value => {
  const pair = toNumberPair(value);
  if (!pair || !isLatLon(pair)) {
    return null;
  }
  return {
    lonLat: [pair[1], pair[0]],
    mapCoordinates: pair,
  };
};

const getCoordinatesFromObject = value => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const lat = value.lat ?? value.latitude;
  const lon = value.lon ?? value.lng ?? value.longitude;
  if (typeof lat !== 'undefined' && typeof lon !== 'undefined') {
    return fromLatLon([lat, lon]);
  }
  return null;
};

export const getEvacuationApplicationCoordinates = application => {
  if (!application) {
    return null;
  }

  const location = parseJsonObject(application.location);
  const geometry = parseJsonObject(location?.geometry);

  if (location?.type === 'Point') {
    const result = fromLonLat(location.coordinates);
    if (result) {
      return result;
    }
  }

  if (geometry?.type === 'Point') {
    const result = fromLonLat(geometry.coordinates);
    if (result) {
      return result;
    }
  }

  const locationCoordinates = fromLonLat(location?.coordinates);
  if (locationCoordinates) {
    return locationCoordinates;
  }

  const geometryCoordinates = fromLonLat(geometry?.coordinates);
  if (geometryCoordinates) {
    return geometryCoordinates;
  }

  const objectCoordinates = getCoordinatesFromObject(application.coordinates)
    || getCoordinatesFromObject(location)
    || getCoordinatesFromObject(application.gps);
  if (objectCoordinates) {
    return objectCoordinates;
  }

  return fromLatLon(application.coordinates);
};

export const getEvacuationApplicationId = application => (
  application?.uuid || application?.violation_id || application?.id || ''
);

export const getEvacuationApplicationDetailsUrl = application => {
  const id = getEvacuationApplicationId(application);
  return id ? `/violations?violation_id=${encodeURIComponent(id)}` : '/violations';
};

export const getEvacuationApplicationColor = application => (
  EVACUATION_APPLICATION_STATUS_COLORS[getViolationStatusDisplayValue(application?.status)] || EVACUATION_APPLICATION_DEFAULT_COLOR
);

export const getEvacuationApplicationMapCoordinates = application => (
  getEvacuationApplicationCoordinates(application)?.mapCoordinates || null
);

export const getEvacuationApplicationLonLat = application => (
  getEvacuationApplicationCoordinates(application)?.lonLat || null
);

export const getEvacuationApplicationsWithCoordinates = applications => (
  (applications || []).filter(application => getEvacuationApplicationMapCoordinates(application))
);

export const getEvacuationApplicationsBounds = applications => {
  const points = getEvacuationApplicationsWithCoordinates(applications)
    .map(getEvacuationApplicationMapCoordinates)
    .filter(Boolean);

  if (!points.length) {
    return null;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;

  points.forEach(([lat, lon]) => {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
  });

  return [
    [minLat, minLon],
    [maxLat, maxLon],
  ];
};

const escapeHtml = value => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const formatCoordinates = application => {
  const coordinates = getEvacuationApplicationLonLat(application);
  if (!coordinates) {
    return '';
  }
  return `${coordinates[1].toFixed(6)}, ${coordinates[0].toFixed(6)}`;
};

export const getEvacuationApplicationTitle = application => {
  const type = application?.violation_type ? localeFormatter('violation.type', application.violation_type) : '';
  return type || application?.description || getEvacuationApplicationId(application) || 'Заявка';
};

export const getEvacuationApplicationSearchText = application => ([
  getEvacuationApplicationId(application),
  application?.vehicle_id,
  application?.provider_name,
  getViolationStatusDisplayValue(application?.status),
  application?.description,
  application?.violation_type,
].filter(Boolean).join(' ').toLowerCase());

export const filterEvacuationApplications = (applications = [], search = '') => {
  const normalizedSearch = String(search || '').trim().toLowerCase();
  if (!normalizedSearch) {
    return applications || [];
  }
  return (applications || []).filter(application => (
    getEvacuationApplicationSearchText(application).includes(normalizedSearch)
  ));
};

const renderPopupRow = (label, value) => {
  if (!value) {
    return '';
  }
  return `<div class="evacuation-popup-row"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`;
};

export const getEvacuationApplicationBalloonData = application => {
  const id = getEvacuationApplicationId(application);
  const status = application?.status ? localeFormatter('violation.status', getViolationStatusDisplayValue(application.status)) : '';
  const type = application?.violation_type ? localeFormatter('violation.type', application.violation_type) : '';
  const coordinates = formatCoordinates(application);
  const url = getEvacuationApplicationDetailsUrl(application);

  return {
    hintContent: escapeHtml(getEvacuationApplicationTitle(application)),
    balloonContentHeader: `<div class="evacuation-popup-title">Заявка ${escapeHtml(id)}</div>`,
    balloonContentBody: [
      renderPopupRow('Тип нарушения', type),
      renderPopupRow('Статус', status || application?.status),
      renderPopupRow('Провайдер', application?.provider_name),
      renderPopupRow('ИД ТС', application?.vehicle_id),
      renderPopupRow('Описание', application?.description),
      renderPopupRow('Координаты', coordinates),
      renderPopupRow('Создана', dateTimeFormatter(application?.created_at)),
      renderPopupRow('Обновлена', dateTimeFormatter(application?.updated_at)),
    ].filter(Boolean).join(''),
    balloonContentFooter: `<a class="btn btn-primary btn-sm evacuation-popup-link" href="${escapeHtml(url)}">Открыть карточку</a>`,
    clusterCaption: `Заявка <strong>${escapeHtml(id)}</strong>`,
  };
};

const getMapBalloonDataFromPlacemarkData = balloonData => ({
  contentHeader: balloonData?.balloonContentHeader || '',
  contentBody: balloonData?.balloonContentBody || '',
  contentFooter: balloonData?.balloonContentFooter || '',
});

export const getEvacuationApplicationMapBalloonData = application => (
  getMapBalloonDataFromPlacemarkData(getEvacuationApplicationBalloonData(application))
);

export const getEvacuationApplicationPlacemarkOptions = application => ({
  preset: 'islands#icon',
  iconColor: getEvacuationApplicationColor(application),
  openBalloonOnClick: false,
});

export const getDefaultEvacuationMapCenter = () => [MOSCOW_CENTER_LON_LAT[1], MOSCOW_CENTER_LON_LAT[0]];
